/**
 * Pure role-guard decision logic, split out from the React hooks so it can
 * be unit-tested without a Clerk/renderer harness.
 *
 * The invariant that matters (INP-502): a guard must redirect a user off a
 * route ONLY when it has POSITIVE evidence they belong to the other
 * audience — never on a transient "unknown" (both role flags false). Clerk
 * briefly reports that unknown state while it refreshes the session on
 * window refocus; treating it as "wrong role" bounced authenticated
 * practitioners off internal pages and dropped their in-progress work.
 */

// Single practitioner domain. The backend's `PRACTITIONER_EMAIL_DOMAINS` can
// be comma-separated, but that env isn't exposed to the SPA build; a second
// domain would need a `VITE_`-injected list here. This guard is UX-only —
// `require_practitioner` on the backend is the authoritative check — so a
// still-unknown domain renders through rather than being wrongly bounced.
export const PRACTITIONER_EMAIL_DOMAIN = '@inputrecovery.com';

export interface RoleFlags {
  isPractitioner: boolean;
  isCustomer: boolean;
}

/** Derive the role flags from the two stable-ish Clerk inputs. `orgId`
 *  comes from the session (does not blip on token refresh); `email` comes
 *  from the user resource (can blip to null mid-refresh — callers latch it,
 *  see useCurrentRole). Org binding wins, matching the backend's
 *  `_provision_clerk_user` order. */
export function deriveRoleFlags(email: string | null, orgId: string | null): RoleFlags {
  const isCustomer = !!orgId;
  const isPractitioner =
    !!email && !isCustomer && email.toLowerCase().endsWith(PRACTITIONER_EMAIL_DOMAIN);
  return { isPractitioner, isCustomer };
}

export interface RoleView extends RoleFlags {
  isAuthReady: boolean;
  isSignedIn: boolean;
}

export interface EmailLatch {
  userId: string | null;
  email: string | null;
}

/** Keep the last known email for a user across a transient `useUser()`
 *  reload. Clerk nulls `user` (hence `liveEmail`) for a few hundred ms while
 *  it refreshes the session; without latching, that flips `isPractitioner`
 *  false and bounces the user (INP-502). Email is immutable per session, so
 *  a known-good value is safe to keep until the userId changes or the live
 *  value becomes a *different* non-null email. A null live email never
 *  clears the latch. */
export function latchEmail(prev: EmailLatch, next: { userId: string | null; liveEmail: string | null }): EmailLatch {
  if (next.userId !== prev.userId) return { userId: next.userId, email: next.liveEmail };
  if (next.liveEmail && next.liveEmail !== prev.email) {
    return { userId: next.userId, email: next.liveEmail };
  }
  return prev;
}

export type GuardOutcome =
  | 'wait'
  | 'render'
  | 'redirect-sign-in'
  | 'redirect-portal'
  | 'redirect-customers';

/** What a RoleGuard should do for the given audience + role view.
 *
 *  - `wait`   — Clerk hasn't loaded; render nothing briefly.
 *  - `render` — show the children (right audience, OR an as-yet-unknown role;
 *    the backend is the real authorization boundary, so rendering an
 *    unknown-role user's page is safe and avoids bouncing on a refresh blip).
 *  - `redirect-sign-in` — no session at all, on a PRACTITIONER route.
 *    Those pages have no signed-out UI: rendering them just mounts queries
 *    that hang tokenless forever, so send the user to /sign-in (the guard
 *    carries the deep link, INP-626).
 *  - `redirect-*` — a CONFIRMED wrong-audience user; send them home. */
export function roleGuardOutcome(
  audience: 'practitioner' | 'customer',
  role: RoleView,
): GuardOutcome {
  if (!role.isAuthReady) return 'wait';
  if (!role.isSignedIn) {
    // The customer surface (/portal) renders its OWN signed-out auth UI —
    // including the Clerk organization-invitation SignUp, which reads the
    // `__clerk_ticket` from the URL. Redirecting to /sign-in would drop
    // that ticket and dead-end a freshly-invited customer with "account
    // not found" (INP-703). Practitioner routes have no signed-out UI, so
    // they still bounce to /sign-in carrying the deep link (INP-626).
    return audience === 'customer' ? 'render' : 'redirect-sign-in';
  }
  if (audience === 'practitioner') {
    return role.isCustomer ? 'redirect-portal' : 'render';
  }
  return role.isPractitioner ? 'redirect-customers' : 'render';
}

/** Validate a post-sign-in return target before navigating to it.
 *
 *  Accepts only a same-origin absolute path (`/runs/…?tab=audit`); anything
 *  else returns null and the caller falls back to the role-home dispatch.
 *  Rejected shapes:
 *  - absolute / protocol-relative URLs (`https://evil`, `//evil`) — open
 *    redirect via the `redirect` query param
 *  - backslash variants (`/\evil`) — browsers normalize `\` to `/` in URLs,
 *    turning them protocol-relative
 *  - `/sign-in*` — would loop the sign-in flow into itself
 *  - bare `/` — the role dispatch picks a better landing than the index */
export function sanitizeReturnTo(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  if (raw === '/' || raw === '/sign-in' || /^\/sign-in[/?#]/.test(raw)) return null;
  return raw;
}

/** The Clerk `forceRedirectUrl` for the sign-in flow: `/sign-in/done`,
 *  carrying an already-sanitized return target when there is one. */
export function signInDoneUrl(returnTo: string | null): string {
  return returnTo
    ? `/sign-in/done?redirect=${encodeURIComponent(returnTo)}`
    : '/sign-in/done';
}
